import { useState, useEffect, useCallback } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import mirapassLogo from '../assets/mirapass-logo.png'
import userIcon from '../assets/user-icon.svg'
import shukudaiIcon from '../assets/icons/file-pen-solid.png'
import aiMessageIcon from '../assets/icons/ai-message-solid.png'
import bellIcon from '../assets/icons/bell-solid.png'
import userSolidIcon from '../assets/icons/user-solid.png'
import { AuthService } from '../services/auth'
import type { AuthUser } from '../services/auth'
import { useAnnouncementCount, setAnnouncementCount, AnnouncementReadsService } from '../services/announcementReads'
import { SchoolService } from '../services/schools'
import { getAdminSelectedSchool, setAdminSelectedSchool, useAdminSelectedSchool } from '../utils/adminSchoolContext'
import { supabase } from '../lib/supabase'
import { resolveStoragePublicUrl } from '../utils/storageUrl'
import LoginDialog from './LoginDialog'
import PasswordChangeDialog from './PasswordChangeDialog'

interface HeaderProps {
  showLoginButton?: boolean
  openLoginDialog?: boolean
}

const NAV_COLORS = {
  shukudai:    '#65c1c8',
  miraiWork:   '#b4c865',
  oshirase:    '#ebad47',
  mypage:      '#DA4574',
}

type LogoDisplayMode = 'both' | 'school_only' | 'system'

const SCHOOL_LOGO_STORAGE_KEY = 'mirapass.schoolLogo'

// The cache is also kept in localStorage so that a reload starts out with the school
// logo instead of flashing the MiraPASS-only logo until the school is fetched.
// A user without a school is cached too (schoolId null), so that the header knows
// right away that the MiraPASS logo is the right one.
const readStoredSchoolLogo = (): { schoolId: string | null; logoUrl: string | null; logoDisplayMode: LogoDisplayMode } | null => {
  try {
    const raw = localStorage.getItem(SCHOOL_LOGO_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && (typeof parsed.schoolId === 'string' || parsed.schoolId === null) ? parsed : null
  } catch {
    return null
  }
}

const _storedSchoolLogo = readStoredSchoolLogo()
let _hasSchoolLogoCache = _storedSchoolLogo !== null
let _cachedSchoolId: string | null = _storedSchoolLogo?.schoolId ?? null
let _cachedSchoolLogoUrl: string | null = _storedSchoolLogo?.logoUrl ?? null
let _cachedLogoDisplayMode: LogoDisplayMode = _storedSchoolLogo?.logoDisplayMode ?? 'both'

const saveSchoolLogoCache = (schoolId: string | null, logoUrl: string | null, logoDisplayMode: LogoDisplayMode): void => {
  _hasSchoolLogoCache = true
  _cachedSchoolId = schoolId
  _cachedSchoolLogoUrl = logoUrl
  _cachedLogoDisplayMode = logoDisplayMode
  try {
    localStorage.setItem(SCHOOL_LOGO_STORAGE_KEY, JSON.stringify({ schoolId, logoUrl, logoDisplayMode }))
  } catch {
    // Storage may be unavailable (private mode); the in-memory cache still works.
  }
}

const resetSchoolLogoCache = (): void => {
  _hasSchoolLogoCache = false
  _cachedSchoolId = null
  _cachedSchoolLogoUrl = null
  _cachedLogoDisplayMode = 'both'
  try {
    localStorage.removeItem(SCHOOL_LOGO_STORAGE_KEY)
  } catch {
    // Ignore: nothing to clear if storage is unavailable.
  }
}

// The logo cache lives at module scope so the school logo doesn't flash on every
// navigation. It has to be dropped on sign-out, otherwise the next Header mount
// (landing page, login screen) starts out showing the previous user's school logo.
const clearSchoolLogoCache = (): void => {
  resetSchoolLogoCache()
  setAdminSelectedSchool(null)
}

const Header = ({ showLoginButton = false, openLoginDialog = false }: HeaderProps) => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false)
  const [isPasswordChangeDialogOpen, setIsPasswordChangeDialogOpen] = useState(false)
  const [passwordChangeError, setPasswordChangeError] = useState('')
  // An admin's selected school takes precedence over the user's own school. Start from
  // it on mount, otherwise every page change first renders the MiraPASS-only logo.
  const [schoolLogoUrl, setSchoolLogoUrl] = useState<string | null>(() => getAdminSelectedSchool()?.logoUrl ?? _cachedSchoolLogoUrl)
  const [logoDisplayMode, setLogoDisplayMode] = useState<LogoDisplayMode>(() => getAdminSelectedSchool()?.logoDisplayMode ?? _cachedLogoDisplayMode)
  const [failedSchoolLogoUrl, setFailedSchoolLogoUrl] = useState<string | null>(null)
  const announcementCount = useAnnouncementCount()
  const [isSchoolDialogOpen, setIsSchoolDialogOpen] = useState(false)
  const [schools, setSchools] = useState<{ id: string; name: string }[]>([])
  const [isLoadingSchools, setIsLoadingSchools] = useState(false)
  const [schoolSearch, setSchoolSearch] = useState('')
  const selectedSchool = useAdminSelectedSchool()
  const location = useLocation()
  const navigate = useNavigate()
  const teacherModeParam = new URLSearchParams(location.search).get('teacherMode') === 'true'
  const isTeacherMode = location.pathname === '/teacher' || location.pathname.startsWith('/teacher/') || teacherModeParam
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin'

  const checkAuthState = useCallback(async () => {
    try {
      const user = await AuthService.getCurrentUser()
      setCurrentUser(user)
      if (!user) {
        clearSchoolLogoCache()
        setSchoolLogoUrl(null)
        setLogoDisplayMode('both')
        return
      }
      const adminSchool = getAdminSelectedSchool()
      const usesAdminSchool = adminSchool !== null && (user.role === 'admin' || user.role === 'super_admin')
      // Only touch the displayed logo once the final value is known: resetting it first
      // flashes the MiraPASS-only logo until the fetch below finishes.
      if (!user.school_id) {
        saveSchoolLogoCache(null, null, 'both')
        if (!usesAdminSchool) {
          setSchoolLogoUrl(null)
          setLogoDisplayMode('both')
        }
      } else {
        if (user.school_id !== _cachedSchoolId) {
          // Another school's logo is cached (e.g. a different account): don't show it.
          resetSchoolLogoCache()
          if (!usesAdminSchool) {
            setSchoolLogoUrl(null)
            setLogoDisplayMode('both')
          }
        }
        const { data } = await supabase
          .from('schools')
          .select('logo_url, logo_display_mode')
          .eq('id', user.school_id)
          .single()
        if (data) {
          saveSchoolLogoCache(user.school_id, data.logo_url, data.logo_display_mode as LogoDisplayMode)
          if (!usesAdminSchool) {
            setSchoolLogoUrl(data.logo_url)
            setLogoDisplayMode(data.logo_display_mode as LogoDisplayMode)
          }
        }
      }
      if (adminSchool && usesAdminSchool) {
        // The selection survives reloads, so it carries a snapshot of a school that may
        // have been renamed, re-branded, or deleted since. Refresh it before using it.
        const { data, error } = await supabase
          .from('schools')
          .select('name, logo_url, logo_display_mode, is_admin_school')
          .eq('id', adminSchool.id)
          .maybeSingle()
        if (data) {
          const mode = data.logo_display_mode as 'both' | 'school_only' | 'system'
          const isAdminSchool = data.is_admin_school === true
          if (
            data.name !== adminSchool.name ||
            data.logo_url !== adminSchool.logoUrl ||
            mode !== adminSchool.logoDisplayMode ||
            isAdminSchool !== adminSchool.isAdminSchool
          ) {
            setAdminSelectedSchool({
              id: adminSchool.id,
              name: data.name,
              logoUrl: data.logo_url,
              logoDisplayMode: mode,
              isAdminSchool,
            })
          }
          setSchoolLogoUrl(data.logo_url)
          setLogoDisplayMode(mode)
        } else if (error) {
          // A transient failure shouldn't wipe a working selection.
          setSchoolLogoUrl(adminSchool.logoUrl)
          setLogoDisplayMode(adminSchool.logoDisplayMode)
        } else {
          // The school is gone, or no longer visible to this admin.
          setAdminSelectedSchool(null)
          setSchoolLogoUrl(null)
          setLogoDisplayMode('both')
        }
      } else if (adminSchool) {
        // A non-admin signed in on top of an admin's school selection.
        setAdminSelectedSchool(null)
      }
    } catch (error) {
      console.error('Failed to check auth state:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    checkAuthState()
  }, [checkAuthState])

  // Drop the school branding as soon as the session ends, so a Header that stays
  // mounted (or one mounted right after) never keeps showing the school logo.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event !== 'SIGNED_OUT') return
      clearSchoolLogoCache()
      setCurrentUser(null)
      setSchoolLogoUrl(null)
      setLogoDisplayMode('both')
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    const handler = (e: Event) => {
      const { schoolId, logoUrl, logoDisplayMode } = (e as CustomEvent<{
        schoolId: string
        logoUrl: string | null
        logoDisplayMode?: 'both' | 'school_only' | 'system'
      }>).detail
      if (schoolId === _cachedSchoolId) {
        saveSchoolLogoCache(schoolId, logoUrl, logoDisplayMode ?? _cachedLogoDisplayMode)
      }
      const adminSchool = getAdminSelectedSchool()
      if (adminSchool?.id === schoolId) {
        setAdminSelectedSchool({
          ...adminSchool,
          logoUrl,
          logoDisplayMode: logoDisplayMode ?? adminSchool.logoDisplayMode,
        })
      } else if (!adminSchool && schoolId === _cachedSchoolId) {
        setSchoolLogoUrl(logoUrl)
        if (logoDisplayMode) {
          setLogoDisplayMode(logoDisplayMode)
        }
      }
    }
    window.addEventListener('schoolLogoUpdated', handler)
    return () => window.removeEventListener('schoolLogoUpdated', handler)
  }, [])

  useEffect(() => {
    if (currentUser) {
      AnnouncementReadsService.getUnreadCount(currentUser)
        .then(setAnnouncementCount)
        .catch(() => {})
    }
  }, [currentUser])

  useEffect(() => {
    if (openLoginDialog && !currentUser && !isLoading) {
      setIsLoginDialogOpen(true)
    }
  }, [openLoginDialog, currentUser, isLoading])

  const handlePasswordChangeSuccess = async () => {
    setIsPasswordChangeDialogOpen(false)
    setPasswordChangeError('')
    await checkAuthState()
  }

  const handlePasswordChangeError = (message: string) => {
    setPasswordChangeError(message)
  }

  const openSchoolDialog = async () => {
    setIsSchoolDialogOpen(true)
    setSchoolSearch('')
    if (schools.length === 0) {
      setIsLoadingSchools(true)
      try {
        const data = await SchoolService.getSchools()
        setSchools(data.map(s => ({ id: s.id, name: s.name })))
      } catch (error) {
        console.error('Failed to load schools:', error)
      } finally {
        setIsLoadingSchools(false)
      }
    }
  }

  const handleSelectSchool = async (school: { id: string; name: string } | null) => {
    setIsSchoolDialogOpen(false)
    if (!school) {
      setAdminSelectedSchool(null)
      return
    }
    const { data } = await supabase
      .from('schools')
      .select('logo_url, logo_display_mode, is_admin_school')
      .eq('id', school.id)
      .single()
    setAdminSelectedSchool({
      id: school.id,
      name: school.name,
      logoUrl: data?.logo_url ?? null,
      logoDisplayMode: (data?.logo_display_mode ?? 'both') as 'both' | 'school_only' | 'system',
      isAdminSchool: data?.is_admin_school === true,
    })
  }

  useEffect(() => {
    if (selectedSchool) {
      setSchoolLogoUrl(selectedSchool.logoUrl)
      setLogoDisplayMode(selectedSchool.logoDisplayMode)
    } else {
      setSchoolLogoUrl(_cachedSchoolLogoUrl)
      setLogoDisplayMode(_cachedLogoDisplayMode)
    }
  }, [selectedSchool])

  const filteredSchools = schools.filter(s =>
    s.name.toLowerCase().includes(schoolSearch.toLowerCase())
  )

  const badgeCount = announcementCount > 99 ? '99+' : String(announcementCount)

  // Until we know which school's branding applies, show no logo rather than the
  // MiraPASS-only one, which would then switch to the school's logo.
  const isLogoKnown = !isLoading || _hasSchoolLogoCache || selectedSchool !== null

  // A logo that fails to load would otherwise leave its alt text in the header; fall
  // back to the MiraPASS-only logo instead.
  const resolvedSchoolLogoUrl = schoolLogoUrl ? resolveStoragePublicUrl(schoolLogoUrl) : null
  const displayedSchoolLogoUrl = resolvedSchoolLogoUrl !== failedSchoolLogoUrl ? resolvedSchoolLogoUrl : null

  return (
    <>
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          {/* 管理者は右端が2段になるので、py-5 の行と同じ 72px に高さを固定して
              先生でログインしたときとヘッダーの高さが変わらないようにする */}
          {/* lg 未満（iPad 縦など）はロゴ・ナビを一段小さくして、折り返しや重なりを防ぐ */}
          <div className={`flex justify-between items-center gap-3 ${isAdmin ? 'h-[72px]' : 'py-5'}`}>

            {/* Logo */}
            <div className="flex items-center gap-3 min-w-0 shrink">
              <Link to={isTeacherMode ? '/teacher' : '/videos'} className="flex items-center gap-2 lg:gap-3 min-w-0">
                {!isLogoKnown ? (
                  <span className="block h-5 lg:h-7" aria-hidden="true" />
                ) : logoDisplayMode === 'both' && displayedSchoolLogoUrl ? (
                  <>
                    {/* Both logos share one height, 10% smaller than when a logo stands alone.
                        The height is a max (h-auto) so that when a narrow window squeezes the
                        width, the height follows and the logo keeps its aspect ratio. */}
                    <img src={displayedSchoolLogoUrl} alt="学校ロゴ" onError={() => setFailedSchoolLogoUrl(displayedSchoolLogoUrl)} className="h-auto max-h-[18px] lg:max-h-[25px] w-auto min-w-0" />
                    <span className="text-gray-400 text-sm lg:text-lg font-bold select-none">×</span>
                    <img src={mirapassLogo} alt="MiraPASS" className="h-auto max-h-[18px] lg:max-h-[25px] w-auto min-w-0" />
                  </>
                ) : logoDisplayMode === 'school_only' && displayedSchoolLogoUrl ? (
                  <img src={displayedSchoolLogoUrl} alt="学校ロゴ" onError={() => setFailedSchoolLogoUrl(displayedSchoolLogoUrl)} className="h-auto max-h-5 lg:max-h-7 w-auto min-w-0" />
                ) : (
                  <img src={mirapassLogo} alt="MiraPASS" className="h-auto max-h-5 lg:max-h-7 w-auto min-w-0" />
                )}
              </Link>
            </div>

            {/* Right nav */}
            <div className="flex items-center gap-3 lg:gap-5 shrink-0">
              {!isLoading && currentUser ? (
                <>
                  {/* みらいワーク */}
                  {!isTeacherMode && (
                    <Link to="/chat" state={{ newChat: true }} className="flex items-center gap-1 whitespace-nowrap hover:opacity-75 transition-opacity">
                      <img src={aiMessageIcon} alt="みらいワーク" className="w-5 h-5 lg:w-6 lg:h-6" />
                      <span className="text-xs lg:text-sm font-semibold" style={{ color: NAV_COLORS.miraiWork }}>みらいワーク</span>
                    </Link>
                  )}

                  {/* 宿題 */}
                  <Link to={isTeacherMode ? '/teacher/homework' : '/homework'} className="flex items-center gap-1 whitespace-nowrap hover:opacity-75 transition-opacity">
                    <img src={shukudaiIcon} alt="宿題" className="w-5 h-5 lg:w-6 lg:h-6" />
                    <span className="text-xs lg:text-sm font-semibold" style={{ color: NAV_COLORS.shukudai }}>宿題</span>
                  </Link>

                  {/* お知らせ */}
                  {!isTeacherMode && (
                    <Link to="/announcements" className="flex items-center gap-1 whitespace-nowrap hover:opacity-75 transition-opacity">
                      <div className="relative">
                        <img src={bellIcon} alt="お知らせ" className="w-5 h-5 lg:w-6 lg:h-6" />
                        {announcementCount > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center leading-none px-0.5">
                            {badgeCount}
                          </span>
                        )}
                      </div>
                      <span className="text-xs lg:text-sm font-semibold" style={{ color: NAV_COLORS.oshirase }}>お知らせ</span>
                    </Link>
                  )}

                  {/* マイページ / 設定 */}
                  <Link
                    to={isTeacherMode ? '/mypage?teacherMode=true' : '/mypage'}
                    className="flex items-center gap-1 whitespace-nowrap hover:opacity-75 transition-opacity"
                  >
                    <img src={userIcon} alt={isTeacherMode ? '設定' : 'マイページ'} className="w-5 h-5 lg:w-6 lg:h-6" />
                    <span className="text-xs lg:text-sm font-semibold" style={{ color: NAV_COLORS.mypage }}>{isTeacherMode ? '設定' : 'マイページ'}</span>
                  </Link>

                  {/* 先生モード + 学校切替。管理者は学校選択を上、モード切替を下に縦積みし、
                      2段でも header が伸びすぎないよう両方のボタンを一段小さくする */}
                  {['teacher', 'admin', 'super_admin'].includes(currentUser.role) && (
                    <div className={isAdmin ? 'flex flex-col items-stretch gap-1' : 'flex items-center'}>
                      {/* 学校切替 (admins only) */}
                      {isAdmin && (
                        <button
                          onClick={openSchoolDialog}
                          className="flex h-7 items-center justify-center gap-1.5 px-2.5 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <span className="truncate max-w-[110px] lg:max-w-[150px]">{selectedSchool ? selectedSchool.name : '学校を選択'}</span>
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}

                      <button
                        onClick={() => navigate(isTeacherMode ? '/videos' : '/teacher')}
                        className={`flex items-center justify-center gap-1.5 rounded-lg font-semibold text-white transition-colors ${
                          isAdmin ? 'h-7 px-2.5 text-xs' : 'px-2 py-1 text-xs lg:px-3 lg:py-1.5 lg:text-sm'
                        } whitespace-nowrap ${isTeacherMode ? 'hover:opacity-90' : 'bg-blue-500 hover:bg-blue-600'}`}
                        style={isTeacherMode ? { backgroundColor: '#da4574' } : undefined}
                      >
                        <img src={userSolidIcon} alt="" className={`${isAdmin ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} style={{ filter: 'brightness(0) invert(1)' }} />
                        {isTeacherMode ? '生徒モード' : '先生モード'}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                showLoginButton && (
                  <div className="flex gap-2">
                    <a
                      href="https://mirasso.co.jp/contact"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-[#da4574] text-white rounded font-semibold hover:bg-[#c63a68] transition-colors"
                    >
                      お問い合わせ
                    </a>
                    <button
                      onClick={() => setIsLoginDialogOpen(true)}
                      className="px-4 py-2 bg-[#da4574] text-white rounded font-semibold hover:bg-[#c63a68] transition-colors"
                    >
                      ログイン
                    </button>
                  </div>
                )
              )}
            </div>

          </div>
        </div>
      </header>

      {/* School switch dialog */}
      {isSchoolDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setIsSchoolDialogOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 flex flex-col max-h-[70vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="text-base font-semibold text-gray-900">学校を切り替え</h2>
              <button onClick={() => setIsSchoolDialogOpen(false)} className="text-gray-400 hover:text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="px-4 py-3 border-b">
              <input
                type="text"
                placeholder="学校名で検索..."
                value={schoolSearch}
                onChange={e => setSchoolSearch(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div className="overflow-y-auto flex-1">
              {isLoadingSchools ? (
                <div className="flex items-center justify-center py-8 text-sm text-gray-500">読み込み中...</div>
              ) : (
                <ul>
                  <li>
                    <button
                      onClick={() => handleSelectSchool(null)}
                      className={`w-full text-left px-5 py-3 text-sm hover:bg-gray-50 transition-colors ${!selectedSchool ? 'font-semibold text-blue-600 bg-blue-50' : 'text-gray-700'}`}
                    >
                      すべての学校
                    </button>
                  </li>
                  {filteredSchools.map(school => (
                    <li key={school.id}>
                      <button
                        onClick={() => handleSelectSchool(school)}
                        className={`w-full text-left px-5 py-3 text-sm hover:bg-gray-50 transition-colors ${selectedSchool?.id === school.id ? 'font-semibold text-blue-600 bg-blue-50' : 'text-gray-700'}`}
                      >
                        {school.name}
                      </button>
                    </li>
                  ))}
                  {filteredSchools.length === 0 && (
                    <li className="px-5 py-4 text-sm text-gray-400 text-center">該当する学校がありません</li>
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <LoginDialog
        isOpen={isLoginDialogOpen}
        onClose={() => setIsLoginDialogOpen(false)}
      />

      <PasswordChangeDialog
        isOpen={isPasswordChangeDialogOpen}
        onSuccess={handlePasswordChangeSuccess}
        onError={handlePasswordChangeError}
        onClose={() => setIsPasswordChangeDialogOpen(false)}
        isMandatory={true}
      />

      {passwordChangeError && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 z-50 max-w-md">
          <div className="text-red-800 text-sm font-medium mb-2">パスワード変更エラー</div>
          <div className="text-red-700 text-sm">{passwordChangeError}</div>
          <button
            onClick={() => setPasswordChangeError('')}
            className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
          >
            閉じる
          </button>
        </div>
      )}
    </>
  )
}

export default Header
